import { useCallback, useEffect, useMemo, useState } from "react"

function detectStandaloneMode() {
  if (typeof window === "undefined") return false

  const mediaStandalone = window.matchMedia?.("(display-mode: standalone)")?.matches
  const iosStandalone = window.navigator?.standalone === true
  return Boolean(mediaStandalone || iosStandalone)
}

export default function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null)
  const [isInstalled, setIsInstalled] = useState(() => detectStandaloneMode())

  useEffect(() => {
    if (typeof window === "undefined") return undefined

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault()
      if (detectStandaloneMode()) {
        setIsInstalled(true)
        setDeferredPrompt(null)
        return
      }
      setDeferredPrompt(event)
    }

    const handleAppInstalled = () => {
      setIsInstalled(true)
      setDeferredPrompt(null)
    }

    const handleDisplayModeChange = () => {
      if (detectStandaloneMode()) {
        setIsInstalled(true)
        setDeferredPrompt(null)
      }
    }

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt)
    window.addEventListener("appinstalled", handleAppInstalled)

    const mediaQuery = window.matchMedia?.("(display-mode: standalone)")
    mediaQuery?.addEventListener?.("change", handleDisplayModeChange)

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt)
      window.removeEventListener("appinstalled", handleAppInstalled)
      mediaQuery?.removeEventListener?.("change", handleDisplayModeChange)
    }
  }, [])

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return false

    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    const accepted = outcome === "accepted"

    if (accepted || detectStandaloneMode()) {
      setIsInstalled(true)
    }

    setDeferredPrompt(null)
    return accepted
  }, [deferredPrompt])

  const isInstallable = useMemo(() => Boolean(deferredPrompt) && !isInstalled, [deferredPrompt, isInstalled])

  return {
    isInstallable,
    isInstalled,
    promptInstall,
  }
}
